
import { useState } from 'react';

interface SearchFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  priceRange: [number, number];
  onPriceRangeChange: (range: [number, number]) => void;
  selectedBrands: string[];
  onBrandsChange: (brands: string[]) => void;
  minRating: number;
  onMinRatingChange: (rating: number) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  categories: string[];
  brands: string[];
  maxPrice: number;
  onClearFilters: () => void;
}

const SearchFilters = ({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  priceRange,
  onPriceRangeChange,
  selectedBrands,
  onBrandsChange,
  minRating,
  onMinRatingChange,
  sortBy,
  onSortChange,
  categories,
  brands,
  maxPrice,
  onClearFilters,
}: SearchFiltersProps) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [localMinPrice, setLocalMinPrice] = useState(priceRange[0].toString());
  const [localMaxPrice, setLocalMaxPrice] = useState(priceRange[1].toString());

  const handleBrandToggle = (brand: string) => {
    if (selectedBrands.includes(brand)) {
      onBrandsChange(selectedBrands.filter(b => b !== brand));
    } else {
      onBrandsChange([...selectedBrands, brand]);
    }
  };

  const handlePriceApply = () => {
    const min = Math.max(0, parseInt(localMinPrice) || 0);
    const max = Math.min(maxPrice, parseInt(localMaxPrice) || maxPrice);
    onPriceRangeChange([min, max]);
  };

  const activeFiltersCount = [
    selectedCategory !== 'All',
    priceRange[0] > 0 || priceRange[1] < maxPrice,
    selectedBrands.length > 0,
    minRating > 0,
  ].filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search products..."
          className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        )}
      </div>

      {/* Sort & Filter Toggle */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm ${
            isFiltersOpen || activeFiltersCount > 0
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <i className="ri-filter-3-line"></i>
          Filters
          {activeFiltersCount > 0 && (
            <span className="w-5 h-5 bg-teal-600 text-white text-xs rounded-full flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </button>

        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="relevance">Sort: Relevance</option>
          <option value="price-low">Price: Low to High</option>
          <option value="price-high">Price: High to Low</option>
          <option value="discount">Biggest Discount</option>
          <option value="name">Name: A-Z</option>
        </select>
      </div>

      {/* Expandable Filters Panel */}
      {isFiltersOpen && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-6 animate-fadeIn">
          {/* Categories */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Category</h4>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => onCategoryChange(category)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Price Range</h4>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  type="number"
                  value={localMinPrice}
                  onChange={(e) => setLocalMinPrice(e.target.value)}
                  placeholder="Min"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <span className="text-gray-400">-</span>
              <div className="flex-1">
                <input
                  type="number"
                  value={localMaxPrice}
                  onChange={(e) => setLocalMaxPrice(e.target.value)}
                  placeholder="Max"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <button
                onClick={handlePriceApply}
                className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Apply
              </button>
            </div>
          </div>

          {/* Brands */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Brand</h4>
            <div className="grid grid-cols-2 gap-2">
              {brands.map(brand => (
                <label
                  key={brand}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedBrands.includes(brand)}
                    onChange={() => handleBrandToggle(brand)}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <span className="text-sm text-gray-700">{brand}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Minimum Rating</h4>
            <div className="flex gap-2">
              {[0, 3, 4, 4.5].map(rating => (
                <button
                  key={rating}
                  onClick={() => onMinRatingChange(rating)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    minRating === rating
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {rating === 0 ? (
                    'Any'
                  ) : (
                    <>
                      <i className="ri-star-fill text-amber-400"></i>
                      {rating}+
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <button
              onClick={onClearFilters}
              className="w-full py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm font-medium"
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* Active Filter Tags */}
      {activeFiltersCount > 0 && !isFiltersOpen && (
        <div className="flex flex-wrap gap-2">
          {selectedCategory !== 'All' && (
            <span className="flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
              {selectedCategory}
              <button
                onClick={() => onCategoryChange('All')}
                className="hover:text-teal-900 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </span>
          )}
          {(priceRange[0] > 0 || priceRange[1] < maxPrice) && (
            <span className="flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
              ${priceRange[0]} - ${priceRange[1]}
              <button
                onClick={() => onPriceRangeChange([0, maxPrice])}
                className="hover:text-teal-900 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </span>
          )}
          {selectedBrands.map(brand => (
            <span
              key={brand}
              className="flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
            >
              {brand}
              <button
                onClick={() => handleBrandToggle(brand)}
                className="hover:text-teal-900 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </span>
          ))}
          {minRating > 0 && (
            <span className="flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm">
              <i className="ri-star-fill text-amber-400"></i>
              {minRating}+
              <button
                onClick={() => onMinRatingChange(0)}
                className="hover:text-teal-900 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchFilters;
